import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Defect, DefectFormValues, DefectTypeEnum } from '../types';
import DefectAutotLinkPatternItem from './DefectAutoLinkPatternItem';
import { useDispatch } from 'react-redux';
import { addDefectRequest, editDefectRequest } from '../store';
import ModalConfirm from '@shared/components/modals/ModalConfirm';
import { convertToTranslationKey, toTitleCase, uuidv4 } from '@shared/utils/utilsOld';
import JiraIssueSelector from './JiraIssueSelector';
import UtmsChip from '@shared/components/ui/UtmsChip';
import { DEFECT_TYPE_COLOR, defectTypes } from '../constants';

const defectDefaultValues: DefectFormValues = {
    name: '',
    description: '',
    jiraIssue: null,
    type: DefectTypeEnum.AUTOTEST,
    autoLinkPatterns: [],
};

const FORM_ID = 'defect-form';

interface DefectFormDialogProps {
    open: boolean;
    onClose: () => void;
    mode: 'create' | 'edit';
    defect?: Defect | null;
}

const DefectFormDialog: FC<DefectFormDialogProps> = ({ open, onClose, mode, defect }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [openCancelDefectModal, setOpenCancelDefectModal] = useState(false);
    const initialValues: DefectFormValues = useMemo(
        () =>
            defect
                ? {
                      name: defect.name,
                      description: defect.description,
                      jiraIssue: defect.jiraIssueKey
                          ? { jiraLink: defect.jiraIssueUrl ?? '', key: defect.jiraIssueKey, name: '' }
                          : null,
                      type: defect.type,
                      autoLinkPatterns: defect.autoLinkPatterns,
                  }
                : defectDefaultValues,
        [defect],
    );

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
    } = useForm<DefectFormValues>({
        defaultValues: initialValues,
        mode: 'onSubmit',
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'autoLinkPatterns',
    });

    useEffect(() => {
        if (open) {
            reset(initialValues);
        }
    }, [initialValues, open, reset]);

    const resetForm = useCallback(() => {
        const values: DefectFormValues =
            mode === 'edit' && defect
                ? {
                      name: defect.name,
                      description: defect.description || '',
                      jiraIssue: null,
                      type: defect.type,
                      autoLinkPatterns: defect.autoLinkPatterns || [],
                  }
                : defectDefaultValues;

        reset(values);
    }, [defect, mode, reset]);

    const onSubmit = (mode: 'create' | 'edit') => (data: DefectFormValues) => {
        const payload = {
            ...data,
            jiraIssueKey: data.jiraIssue?.key ?? '',
            jiraIssueUrl: data.jiraIssue?.jiraLink ?? '',
        };

        if (mode === 'create') {
            dispatch(addDefectRequest(payload));
        }
        if (mode === 'edit' && defect) {
            dispatch(editDefectRequest({ ...payload, pk: defect.pk }));
        }
        resetForm();
        onClose();
    };

    const handleRemoveAutoLinkPattern = (index: number) => () => {
        remove(index);
    };

    const handleAppendAutoLinkPattern = (index: number) => () => {
        append({
            pk: uuidv4(),
            name: `Rule ${index + 1}`,
            errorMessagePattern: '',
            stackTracePattern: '',
        });
    };

    const handleCloseCancelDefectsModal = () => {
        setOpenCancelDefectModal(false);
    };

    const handleSubmitCancelDefectModal = () => {
        setOpenCancelDefectModal(false);
        resetForm();
        onClose();
    };

    const handleOpenCancelDefectModal = () => {
        setOpenCancelDefectModal(true);
    };

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                resetForm();
                onClose();
            }}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>
                {mode === 'create' ? t('add') : t('edit')} {t('defect').toLowerCase()}
            </DialogTitle>
            <DialogContent sx={{ overflowY: 'visible' }}>
                <Box
                    id={FORM_ID}
                    component="form"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                    onSubmit={handleSubmit(onSubmit(mode))}
                >
                    <Controller
                        name="name"
                        control={control}
                        rules={{
                            required: t('field_required'),
                            minLength: {
                                value: 3,
                                message: t('min_length', { count: 3 }),
                            },
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                size="small"
                                label={t('name') + '*'}
                                error={Boolean(errors.name)}
                                helperText={errors.name?.message}
                                autoFocus
                            />
                        )}
                    />
                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <TextField {...field} size="small" label={t('description')} multiline maxRows={8} />
                        )}
                    />

                    <Controller
                        name="jiraIssue"
                        control={control}
                        render={({ field }) => <JiraIssueSelector value={field.value} onChange={field.onChange} />}
                    />
                    <Controller
                        name="type"
                        control={control}
                        render={({ field }) => (
                            <FormControl size="small" fullWidth>
                                <InputLabel>{t('type')}</InputLabel>
                                <Select {...field} label={t('type')}>
                                    {defectTypes.map((type) => (
                                        <MenuItem value={type}>
                                            <UtmsChip
                                                label={toTitleCase(t(convertToTranslationKey(type)))}
                                                color={DEFECT_TYPE_COLOR[type]}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', fontWeight: 700, gap: 1 }}>
                        <Box>{t('automation_rules')}</Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                maxHeight: 400,
                                overflowY: 'auto',
                                pt: '10px',
                                mt: '-10px',
                            }}
                        >
                            {fields.map((field, index) => (
                                <DefectAutotLinkPatternItem
                                    key={field.id}
                                    index={index}
                                    control={control}
                                    errors={errors}
                                    onRemove={handleRemoveAutoLinkPattern(index)}
                                />
                            ))}
                        </Box>

                        <Box>
                            <UtmsButton
                                type="button"
                                startIcon={<AddIcon />}
                                onClick={handleAppendAutoLinkPattern(fields.length)}
                            >
                                {t('add')} {t('automation_rule')}
                            </UtmsButton>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <UtmsButton form={FORM_ID} type="submit" disabled={!isDirty}>
                    {t('save')}
                </UtmsButton>

                <UtmsButton color="secondary" onClick={isDirty ? handleOpenCancelDefectModal : onClose}>
                    {t('cancel')}
                </UtmsButton>
            </DialogActions>

            <ModalConfirm
                open={openCancelDefectModal}
                handleClose={handleCloseCancelDefectsModal}
                handleSubmit={handleSubmitCancelDefectModal}
                question={t('want_to_cancel_defect')}
                message={t('unsaved_changes_lost')}
                action={t('cancel')}
                close={t('continue_edit')}
            />
        </Dialog>
    );
};

export default DefectFormDialog;
